import { useState } from "react";
import { ChevronRight, Clock, Hourglass } from "lucide-react";
import { formatDuration, formatTime } from "../executionTimeline";

// Un turno es una espera y el trabajo que corrió cuando esa espera se
// destrabó. La espera va delante porque es lo que da inicio al turno.

const RUN_TONE = {
  completed: "bg-emerald-500",
  failed: "bg-rose-500",
  running: "bg-blue-500 animate-pulse",
};

// Cómo terminó una espera, dicho en una línea.
const WAIT_ENDINGS = {
  resumed: "resumed by event",
  flushed: "message burst flushed",
  expired: "timed out",
  cancelled: "cancelled while waiting",
  waiting: "still waiting",
};

const waitEnding = (wait) => {
  const base = WAIT_ENDINGS[wait.status] || wait.status || "ended";
  const detail = wait.resumed_by?.detail;
  return detail && wait.status === "resumed" ? `${base} · ${detail}` : base;
};

/**
 * Un bloque por corrida. Es un conteo, no un eje de tiempo: los bloques miden
 * todos igual porque una corrida de 97 ms y una de 8 s son, acá, una corrida
 * cada una. La duración va escrita al lado.
 */
const RunStrip = ({ runs, onSelectEntry }) => (
  <div className="flex flex-wrap gap-[3px]">
    {runs.map((run) => (
      <button
        key={run.seq}
        type="button"
        onClick={() => onSelectEntry?.(run)}
        title={`${run.name} · ${formatDuration(run.duration_ms)} · ${run.status}`}
        className={`h-4 w-1.5 rounded-[2px] transition-transform hover:scale-y-125 ${
          RUN_TONE[run.status] || "bg-zinc-600"
        }`}
      />
    ))}
  </div>
);

const ExpandedRuns = ({ runs, onSelectEntry }) => (
  <div className="mt-3 divide-y divide-white/5 border-t border-white/5">
    {runs.map((run) => (
      <button
        key={run.seq}
        type="button"
        onClick={() => onSelectEntry?.(run)}
        className="flex w-full items-center gap-3 px-1 py-1.5 text-left hover:bg-white/5"
      >
        <span className="w-10 shrink-0 font-mono text-[10px] text-gray-600">#{run.seq}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-gray-200">
          {run.name}
          {run.pass_number > 1 && (
            <span className="ml-1.5 text-[10px] text-violet-400">pass {run.pass_number}</span>
          )}
        </span>
        <span className="w-28 shrink-0 truncate text-[10px] text-gray-500">{run.type}</span>
        <span className="w-16 shrink-0 text-right text-[11px] text-gray-400">
          {formatDuration(run.duration_ms)}
        </span>
        <span
          className={`w-14 shrink-0 text-right text-[10px] ${
            run.status === "failed" ? "text-rose-400" : "text-gray-500"
          }`}
        >
          {run.status}
        </span>
      </button>
    ))}
  </div>
);

export default function TurnCard({ turn, expanded, onToggle, onSelectEntry }) {
  const [hovered, setHovered] = useState(false);
  const { openingWait, runs } = turn;

  return (
    <div
      className={`rounded-xl border bg-zinc-900/40 transition-colors ${
        turn.open ? "border-violet-500/40" : "border-white/10"
      } ${hovered ? "bg-zinc-900/70" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={runs.length === 0}
        className="flex w-full items-center gap-4 px-4 py-3 text-left disabled:cursor-default"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/40 text-xs font-semibold text-gray-300">
          {turn.index}
        </span>

        <span className="w-40 shrink-0">
          <span className="block text-sm font-medium text-white">Turn {turn.index}</span>
          <span className="block text-[10px] text-gray-500">
            {formatTime(turn.startedAt)}
            {turn.endedAt && ` – ${formatTime(turn.endedAt)}`}
            {!turn.endedAt && runs.length > 0 && " – …"}
          </span>
          <span className="block text-[10px] text-gray-500">
            {runs.length} {runs.length === 1 ? "step run" : "step runs"}
          </span>
        </span>

        <span className="w-52 shrink-0">
          {openingWait ? (
            <>
              <span className="flex items-center gap-1.5 text-xs text-blue-300">
                <Hourglass className="h-3 w-3" />
                {turn.open ? "waiting" : formatDuration(openingWait.duration_ms)}
              </span>
              <span className="mt-0.5 block truncate text-[10px] text-gray-500">
                {waitEnding(openingWait)}
              </span>
            </>
          ) : (
            <span className="text-[10px] text-gray-600">execution start</span>
          )}
        </span>

        <span className="min-w-0 flex-1">
          {runs.length > 0 ? (
            <>
              <RunStrip runs={runs} onSelectEntry={onSelectEntry} />
              <span className="mt-1.5 flex items-center gap-1.5 text-[10px] text-gray-500">
                <Clock className="h-2.5 w-2.5" />
                {formatDuration(turn.runningMs)} working
                {turn.coins > 0 && <span className="text-amber-300/80">· {turn.coins} coins</span>}
                {turn.failed > 0 && <span className="text-rose-400">· {turn.failed} failed</span>}
              </span>
            </>
          ) : (
            // Una espera que terminó sin que corriera nada después. Poner
            // "0 ms working" ahí sugiere que se midió un trabajo que no hubo.
            <span className="text-[10px] text-gray-600">
              {turn.open ? "waiting right now" : "nothing ran after this wait"}
            </span>
          )}
        </span>

        {runs.length > 0 && (
          <ChevronRight
            className={`h-4 w-4 shrink-0 text-gray-600 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          />
        )}
      </button>

      {expanded && runs.length > 0 && (
        <div className="px-4 pb-3">
          <ExpandedRuns runs={runs} onSelectEntry={onSelectEntry} />
        </div>
      )}
    </div>
  );
}
